import React, { useState, useEffect } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  InputGroup,
  Modal
} from "react-bootstrap";
import {
  Save,
  ArrowLeft,
  CheckCircle,
  ExclamationCircle,
 
  Calendar,
  InfoCircle,
  PlayCircle,
  PauseCircle,
  FileText,
  Building,
  House,
  Truck,
  Tag,
  Percent as PercentIcon,
  Check,
 
  PlusCircle
} from "react-bootstrap-icons";

export default function NewTaxe() {
  const navigate = useNavigate();
  
  // États du formulaire
  const [formData, setFormData] = useState({
    code: "",
    nom: "",
    description: "",
    type: "fonciere",
    taux: "",
    devise: "XOF",
    periodicite: "annuelle",
    statut: "active",
    dateDebut: new Date().toISOString().split('T')[0],
    dateFin: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    categorie: "generale"
  });

  // États pour la gestion
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Types de taxes
  const taxTypes = [
    { value: "fonciere", label: "Taxe foncière", icon: <House />, color: "primary" },
    { value: "professionnelle", label: "Taxe professionnelle", icon: <Building />, color: "success" },
    { value: "habitation", label: "Taxe d'habitation", icon: <House />, color: "info" },
    { value: "vehicule", label: "Taxe sur les véhicules", icon: <Truck />, color: "warning" },
    { value: "publicite", label: "Taxe de publicité", icon: <Tag />, color: "dark" },
    { value: "autre", label: "Autre taxe", icon: <Tag />, color: "secondary" }
  ];

  // Périodicités
  const periodicites = [
    { value: "annuelle", label: "Annuelle" },
    { value: "semestrielle", label: "Semestrielle" },
    { value: "trimestrielle", label: "Trimestrielle" },
    { value: "mensuelle", label: "Mensuelle" },
    { value: "ponctuelle", label: "Ponctuelle" }
  ];

  // Catégories
  const categories = [
    { value: "generale", label: "Générale" },
    { value: "specifique", label: "Spécifique" },
    { value: "exceptionnelle", label: "Exceptionnelle" },
    { value: "locale", label: "Locale" },
    { value: "nationale", label: "Nationale" }
  ];

  // Devises
  const devises = [
    { value: "XOF", label: "Franc CFA (XOF)" },
    { value: "EUR", label: "Euro (EUR)" },
    { value: "USD", label: "Dollar (USD)" },
    { value: "XAF", label: "Franc CFA BEAC (XAF)" }
  ];

  // Fonctions de validation
  const validateField = (field, value) => {
    let error = "";
    
    switch(field) {
      case 'code':
        if (!value.trim()) error = "Le code de la taxe est requis";
        else if (!/^[A-Z0-9]{2,10}$/.test(value)) error = "Le code doit contenir 2 à 10 caractères majuscules ou chiffres";
        break;
      case 'nom':
        if (!value.trim()) error = "Le nom de la taxe est requis";
        else if (value.length < 3) error = "Le nom doit contenir au moins 3 caractères";
        else if (value.length > 100) error = "Le nom ne doit pas dépasser 100 caractères";
        break;
      case 'description':
        if (value.length > 500) error = "La description ne doit pas dépasser 500 caractères";
        break;
      case 'type':
        if (!value) error = "Le type de taxe est requis";
        break;
      case 'taux':
        if (!value) error = "Le taux est requis";
        else if (value !== "variable") {
          const num = parseFloat(value);
          if (isNaN(num)) error = "Le taux doit être un nombre";
          else if (num < 0 || num > 100) error = "Le taux doit être entre 0 et 100";
        }
        break;
      case 'devise':
        if (!value) error = "La devise est requise";
        break;
      case 'periodicite':
        if (!value) error = "La périodicité est requise";
        break;
      case 'statut':
        if (!value) error = "Le statut est requis";
        break;
      case 'dateDebut':
        if (!value) error = "La date de début est requise";
        break;
      case 'dateFin':
        if (!value) error = "La date de fin est requise";
        else if (new Date(value) < new Date(formData.dateDebut)) {
          error = "La date de fin doit être après la date de début";
        }
        break;
      case 'categorie':
        if (!value) error = "La catégorie est requise";
        break;
      default:
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    return !error;
  };

  // Validation complète du formulaire
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Valider tous les champs obligatoires
    const requiredFields = ['code', 'nom', 'type', 'taux', 'devise', 'periodicite', 'statut', 'dateDebut', 'dateFin', 'categorie'];
    
    requiredFields.forEach(key => {
      if (!validateField(key, formData[key])) {
        newErrors[key] = errors[key] || "Ce champ est requis";
        isValid = false;
      }
    });
    
    // Validation supplémentaire pour la description
    if (formData.description.length > 500) {
      newErrors.description = "La description ne doit pas dépasser 500 caractères";
      isValid = false;
    }
    
    setErrors(newErrors);
    
    // Marquer tous les champs comme touchés
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    return isValid;
  };

  // Gestion des changements de champ
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validation en temps réel
    if (touched[field]) {
      validateField(field, value);
    }
  };

  // Gestion du focus
  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    validateField(field, formData[field]);
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = validateForm();
    if (!isValid) {
      showAlert("Veuillez corriger les erreurs dans le formulaire", "danger");
      return;
    }

    setLoading(true);

    // Simulation d'enregistrement
    setTimeout(() => {
    
      setLoading(false);
      setShowSuccessModal(true);
    }, 1500);
  };

  // Navigation après succès
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate("/admin/taxes");
  };

  // Gestion des alertes
  const [alert, setAlert] = useState({ show: false, message: "", type: "info" });

  const showAlert = (message, type = "info", duration = 5000) => {
    setAlert({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        hideAlert();
      }, duration);
    }
  };

  const hideAlert = () => {
    setAlert({ show: false, message: "", type: "info" });
  };

  // Prévisualisation
  const previewData = {
    ...formData,
    taux: formData.taux === "variable" ? "Variable" : `${formData.taux}%`,
    dateDebut: new Date(formData.dateDebut).toLocaleDateString('fr-FR'),
    dateFin: new Date(formData.dateFin).toLocaleDateString('fr-FR')
  };

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <div className="container-fluid">
          {/* Alerte */}
          {alert.show && (
            <Alert 
              variant={alert.type} 
              dismissible 
              onClose={hideAlert}
              className="shadow-sm mb-4"
            >
              <div className="d-flex align-items-center">
                {alert.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alert.type === 'danger' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <InfoCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alert.message}</span>
              </div>
            </Alert>
          )}

          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item"><Link to="/admin/taxes">Taxes</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Nouvelle taxe</li>
                  </ol>
                  <h4 className="main-title mb-0">
                    <PlusCircle className="me-2" />
                    Créer une nouvelle taxe
                  </h4>
                  <p className="text-muted mb-0">Remplissez les informations pour créer une nouvelle taxe</p>
                </div>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary" 
                    as={Link} 
                    to="/superAdmin/Taxes"
                    className="d-flex align-items-center"
                  >
                    <ArrowLeft className="me-2" size={16} />
                    Retour
                  </Button>
                  
                  <Button 
                    variant="outline-info"
                    onClick={() => setShowPreview(!showPreview)}
                    className="d-flex align-items-center"
                  >
                    <FileText className="me-2" size={16} />
                    {showPreview ? "Masquer" : "Prévisualiser"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Prévisualisation */}
          {showPreview && (
            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-info shadow-sm">
                  <Card.Header className="bg-info text-white d-flex align-items-center">
                    <FileText className="me-2" />
                    Prévisualisation de la taxe
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Code:</strong>
                          <Badge bg="primary" className="fs-6">{previewData.code}</Badge>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Nom:</strong>
                          <h5 className="mb-0">{previewData.nom}</h5>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Type:</strong>
                          <span>
                            {taxTypes.find(t => t.value === previewData.type)?.icon}
                            <span className="ms-2">
                              {taxTypes.find(t => t.value === previewData.type)?.label}
                            </span>
                          </span>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Taux:</strong>
                          <h4 className="text-primary mb-0">{previewData.taux}</h4>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Période de validité:</strong>
                          <div>
                            <Calendar className="me-2" />
                            Du {previewData.dateDebut} au {previewData.dateFin}
                          </div>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Périodicité:</strong>
                          <Badge bg="light" text="dark" className="fw-medium">
                            {periodicites.find(p => p.value === previewData.periodicite)?.label}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Statut:</strong>
                          <Badge bg={previewData.statut === "active" ? "success" : "secondary"}>
                            {previewData.statut === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <strong className="text-muted d-block">Devise:</strong>
                          <Badge bg="light" text="dark">
                            {devises.find(d => d.value === previewData.devise)?.label}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                    {previewData.description && (
                      <div className="mt-3 pt-3 border-top">
                        <strong className="text-muted d-block mb-2">Description:</strong>
                        <p className="mb-0">{previewData.description}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Formulaire principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white py-3">
                  <div className="d-flex align-items-center">
                    <PlusCircle className="me-2" />
                    <span className="fw-semibold">Informations de la taxe</span>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Row className="mt-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Code de la taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <Tag />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: TF001"
                              value={formData.code}
                              onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                              onBlur={() => handleBlur("code")}
                              isInvalid={!!errors.code && touched.code}
                              maxLength={10}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.code}
                            </Form.Control.Feedback>
                          </InputGroup>
                          <Form.Text className="text-muted">
                            Code unique d'identification de la taxe (2 à 10 caractères majuscules/chiffres)
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Nom de la taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <FileText />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: Taxe Foncière Urbaine"
                              value={formData.nom}
                              onChange={(e) => handleChange("nom", e.target.value)}
                              onBlur={() => handleBlur("nom")}
                              isInvalid={!!errors.nom && touched.nom}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.nom}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Description détaillée de la taxe..."
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            maxLength={500}
                            isInvalid={!!errors.description}
                          />
                          <Form.Text className="text-muted">
                            {formData.description.length}/500 caractères
                          </Form.Text>
                          {errors.description && (
                            <Form.Control.Feedback type="invalid">
                              {errors.description}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>

                        
                      </Col>



                      

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Taux <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <PercentIcon />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: 15.5 ou 'variable'"
                              value={formData.taux}
                              onChange={(e) => handleChange("taux", e.target.value)}
                              onBlur={() => handleBlur("taux")}
                              isInvalid={!!errors.taux && touched.taux}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.taux}
                            </Form.Control.Feedback>
                          </InputGroup>
                          <Form.Text className="text-muted">
                            Pourcentage ou "variable" pour un taux non fixe
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Devise <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.devise}
                            onChange={(e) => handleChange("devise", e.target.value)}
                            onBlur={() => handleBlur("devise")}
                            isInvalid={!!errors.devise && touched.devise}
                          >
                            {devises.map(devise => (
                              <option key={devise.value} value={devise.value}>
                                {devise.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.devise}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Périodicité <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex flex-wrap gap-2">
                            {periodicites.map(period => (
                              <Button
                                key={period.value}
                                variant={formData.periodicite === period.value ? "primary" : "outline-primary"}
                                onClick={() => handleChange("periodicite", period.value)}
                                size="sm"
                                className="d-flex align-items-center"
                              >
                                {formData.periodicite === period.value && <Check className="me-1" size={12} />}
                                {period.label}
                              </Button>
                            ))}
                          </div>
                          {errors.periodicite && touched.periodicite && (
                            <div className="text-danger small mt-1">{errors.periodicite}</div>
                          )}
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Date de début <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup hasValidation>
                                <InputGroup.Text>
                                  <Calendar />
                                </InputGroup.Text>
                                <Form.Control
                                  type="date"
                                  value={formData.dateDebut}
                                  onChange={(e) => handleChange("dateDebut", e.target.value)}
                                  onBlur={() => handleBlur("dateDebut")}
                                  isInvalid={!!errors.dateDebut && touched.dateDebut}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.dateDebut}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Date de fin <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup hasValidation>
                                <InputGroup.Text>
                                  <Calendar />
                                </InputGroup.Text>
                                <Form.Control
                                  type="date"
                                  value={formData.dateFin}
                                  onChange={(e) => handleChange("dateFin", e.target.value)}
                                  onBlur={() => handleBlur("dateFin")}
                                  isInvalid={!!errors.dateFin && touched.dateFin}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.dateFin}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>

                      

                      <Col md={12}>

                      <Form.Group className="mb-3">
                          <Form.Label>
                            Catégorie <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.categorie}
                            onChange={(e) => handleChange("categorie", e.target.value)}
                            onBlur={() => handleBlur("categorie")}
                            isInvalid={!!errors.categorie && touched.categorie}
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.categorie}
                          </Form.Control.Feedback>
                        </Form.Group>



                        <Form.Group className="mb-3">
                          <Form.Label>
                            Type de taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="row g-2">
                            {taxTypes.map((type) => (
                              <div key={type.value} className="col-4">
                                <div
                                  className={`card border ${formData.type === type.value ? 'border-primary border-2' : 'border-light'} shadow-sm`}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleChange("type", type.value)}
                                >
                                  <div className="card-body p-3 text-center">
                                    <div className={`text-${type.color} mb-2`}>
                                      {type.icon}
                                    </div>
                                    <div className="small">{type.label}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {errors.type && touched.type && (
                            <div className="text-danger small mt-1">{errors.type}</div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Statut <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="statut-active"
                                checked={formData.statut === "active"}
                                onChange={() => handleChange("statut", "active")}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="statut-active">
                                <PlayCircle className="text-success me-2" />
                                Active
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="statut-inactive"
                                checked={formData.statut === "inactive"}
                                onChange={() => handleChange("statut", "inactive")}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="statut-inactive">
                                <PauseCircle className="text-secondary me-2" />
                                Inactive
                              </label>
                            </div>
                          </div>
                          {errors.statut && touched.statut && (
                            <div className="text-danger small mt-1">{errors.statut}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Boutons d'action */}
                    <div className="border-top pt-4 mt-4">
                      <div className="d-flex justify-content-between">
                        <Button
                          variant="outline-secondary"
                          as={Link}
                          to="/admin/taxes"
                          className="d-flex align-items-center"
                        >
                          <ArrowLeft className="me-2" />
                          Annuler
                        </Button>

                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          className="d-flex align-items-center"
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              Création en cours...
                            </>
                          ) : (
                            <>
                              <Save className="me-2" />
                              Créer la taxe
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal de succès */}
        <Modal show={showSuccessModal} onHide={handleSuccessClose} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="text-success">
              <CheckCircle className="me-2" />
              Taxe créée avec succès
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <CheckCircle className="display-1 text-success mb-3" />
            <h5>La taxe a été créée avec succès</h5>
            <p className="text-muted">
              <strong>{formData.nom}</strong> a été ajoutée à votre catalogue de taxes.
            </p>
            <div className="mt-4 p-3 bg-light rounded">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Code:</span>
                <strong>{formData.code}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Type:</span>
                <strong>{taxTypes.find(t => t.value === formData.type)?.label}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Taux:</span>
                <strong className="text-primary">
                  {formData.taux === "variable" ? "Variable" : `${formData.taux}%`}
                </strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Statut:</span>
                <Badge bg={formData.statut === "active" ? "success" : "secondary"}>
                  {formData.statut === "active" ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="secondary" onClick={handleSuccessClose} className="me-3">
              Retour à la liste
            </Button>
            <Button variant="primary" onClick={() => {
              setShowSuccessModal(false);
              navigate(`/superAdmin/Taxes/${formData.code}/modifier`);
            }}>
              Modifier la taxe
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}